import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from "react";
import { Field } from "formik";
import { CurrencySymbolMap } from "@reside/forms/dist/templateTypes";
import { FlexRowCenter } from "../../box";
import { FieldBorder } from "../atoms/FieldBorder";
import { FieldError } from "../atoms/field-error";
import { FloatingFieldLabel } from "../atoms/FloatingFieldLabel";
import { isValueFilled } from "../text-field";
import { Input } from "../atoms/TextualInput";
import { Label } from "../atoms/Label";
import { LockIcon } from "../atoms";
import { isInvalid } from "../formHelpers";
export const MATH_MAX_CALL_REGEX = /^(Math.max\()?(\(?[0-9.+\-*/(),]+\)?)+(,0\))?$/;
/**
 * A formik field that computes a value based on a mathematical expression.
 *
 * @param {Props} props The props for the component.
 * @returns {JSX.Element} The rendered computation field component.
 */
export const ComputationField = ({ name, label, placeholder, renderBefore, ...restProps }) => {
    return (_jsx(Field, { name: name, children: ({ field, form }) => {
            return (_jsxs(Label, { children: [_jsx(FloatingFieldLabel, { visible: isValueFilled(field.value), children: label }), _jsxs(FlexRowCenter, { children: [renderBefore(field, restProps), _jsx(ComputationInput, { ...field, ...restProps, update: (value) => form.setFieldValue(name, String(value), false), placeholder: (placeholder ?? typeof label === "string")
                                    ? label
                                    : "", value: field.value }), restProps.disabled && _jsx(LockIcon, {})] }), _jsx(FieldBorder, { focused: false, invalid: isInvalid(name, form) }), _jsx(FieldError, { name: name })] }));
        } }));
};
ComputationField.defaultProps = {
    renderBefore: (field, props) => props.currency && _jsx(_Fragment, { children: CurrencySymbolMap[props.currency] }),
};
const ComputationInput = ({ type, name, value, placeholder, disabled, computation, formatter, fractionalDigits, onBlur, onChange, update, }) => {
    const expression = formatter(computation, 0);
    useEffect(() => {
        const prevValue = String(value);
        const nextValue = evalMathExpression(expression, {
            fractionalDigits,
        });
        if (prevValue !== nextValue) {
            update(nextValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computation, expression, fractionalDigits, update, value]);
    return (_jsx(Input, { type: type, name: name, value: value, disabled: disabled, placeholder: placeholder, onBlur: onBlur, onChange: onChange }));
};
ComputationInput.defaultProps = {
    type: "number",
    onBlur: () => { },
    onChange: () => { },
    fractionalDigits: 2,
};
/**
 * Evaluates a mathematical expression.
 *
 * @param {string} expression The mathematical expression to evaluate.
 * @param {object} [options] The options for the evaluation.
 * @param {number} [options.fractionalDigits=2] The number of fractional digits to round the result to.
 * @returns {string} The result of the evaluation.
 */
export const evalMathExpression = (expression, options = {
    fractionalDigits: 2,
}) => {
    // eslint-disable no-useless-escape
    const expressionToTest = expression
        .replace(/new Date\(\S+\)/gi, "1")
        .replace(/Math.\w+/gi, "2+")
        .replace(/\?/gi, "-")
        .replace(/'[0-9-]*'/gi, "0")
        .replace(/===/gi, "-")
        .replace(/!==/gi, "-");
    if (!MATH_MAX_CALL_REGEX.test(expressionToTest)) {
        return "0";
    }
    // eslint-disable-next-line no-eval
    return String(Number(eval(expression)).toFixed(options.fractionalDigits));
};
